import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { Variants } from 'framer-motion';
import {
    ArrowRight,
    Building2,
    CalendarCheck,
    Check,
    ClipboardList,
    Database,
    FileBadge,
    ListChecks,
    Megaphone,
    MessageCircleQuestionMark,
    Moon,
    PhoneCall,
    Send,
    Sun,
    Sunset,
    Users,
    Zap,
} from 'lucide-react';
import DemoVideo from './ui/DemoVideo';
import SectionHeading from './ui/SectionHeading';
import { Reveal, RevealGroup, RevealItem, itemVariants } from './ui/Reveal';
import { BrandMark } from './ui/BrandLogos';
import FAQ, { type FAQEntry } from './FAQ';
import { track } from '../lib/track';
import { REAL_ESTATE_PATH } from '../lib/router';
import { CALENDLY_URL } from '../lib/calendly';
import { POSTER_URL, VIDEO_URL } from '../lib/videos';
import { WHATSAPP_URL } from '../lib/whatsapp';
import pageMeta from '../lib/pageMeta.json';

const META = pageMeta[REAL_ESTATE_PATH];
const MAX_COMPANY_LENGTH = 60;
const EASE = [0.16, 1, 0.3, 1] as const;

type Placement = 'hero' | 'final' | 'sticky';

/**
 * `?company=` as display text: invisible/control characters removed (they
 * could reorder how the name reads), whitespace collapsed, and capped at 60
 * characters including the ellipsis added when it's cut. It is only ever
 * rendered as a React text node, never as HTML.
 */
function readCompany(): string {
    const raw = new URLSearchParams(window.location.search).get('company') ?? '';
    const chars = Array.from(raw.replace(/[\p{Cc}\p{Cf}]/gu, ' ').replace(/\s+/g, ' ').trim());
    if (chars.length <= MAX_COMPANY_LENGTH) return chars.join('');
    return `${chars.slice(0, MAX_COMPANY_LENGTH - 1).join('').trimEnd()}…`;
}

const steps = [
    {
        icon: Megaphone,
        title: 'We run the ads',
        body: 'Targeted Facebook, Instagram and Google ads reach people in Abuja and the diaspora who want land or homes. We manage everything.',
    },
    {
        icon: ClipboardList,
        title: 'Buyers raise their hand',
        body: 'Interested people fill a short form with budget, preferred location and timeline.',
    },
    {
        icon: PhoneCall,
        title: 'We call in under 2 minutes',
        body: 'Our AI agent calls them any time of day, answers their questions and books an inspection with your sales team.',
    },
];

const callTimes = [
    { icon: Sun, tint: 'text-amber-300', form: '12:00 noon', call: '12:02' },
    { icon: Sunset, tint: 'text-brand-orange', form: '5:00pm', call: '5:02pm' },
    { icon: Moon, tint: 'text-indigo-300', form: '11:30pm', call: '11:32pm' },
];

const agentTasks = [
    { icon: MessageCircleQuestionMark, lead: 'Answers estate questions:', rest: 'location, plot sizes, prices, payment plans' },
    { icon: FileBadge, lead: 'Explains title documents', rest: '(C of O, R of O) the way your team would' },
    { icon: ListChecks, lead: 'Qualifies the buyer:', rest: 'budget, timeline, cash or instalments' },
    { icon: CalendarCheck, lead: 'Books a site inspection', rest: 'straight into your calendar' },
    { icon: Send, lead: 'Hands hot buyers to your sales team', rest: 'on WhatsApp with a call summary' },
    { icon: Database, lead: 'Logs every lead and call recording', rest: 'in your CRM or a Google Sheet' },
];

const weHandle = ['Ad campaigns', 'Landing form', 'AI call agent', 'Follow-up messages', 'Weekly lead report'];
const youHandle = ['Inspections', 'Closing the sale'];

const faqs: FAQEntry[] = [
    {
        q: 'Does the agent sound robotic?',
        a: 'It speaks naturally, and the video above is a real call.',
    },
    {
        q: 'Will people get calls in the middle of the night?',
        a: "Only people who just filled the form, and they choose on the form whether they're happy to be called straight away.",
    },
    {
        q: 'Who owns the leads?',
        a: 'You do. Every lead and recording is yours.',
    },
    {
        q: 'What does the agent know about our estates?',
        a: 'We train it on your price lists, payment plans, titles and FAQs, and you approve the script before launch.',
    },
    {
        q: 'How soon can we launch?',
        a: 'Typically within 1–2 weeks after we receive your estate details.',
    },
    {
        q: 'What about data and consent?',
        a: 'Every form includes a consent checkbox, and we handle data in line with the Nigeria Data Protection Act.',
    },
];

const fadeUp: Variants = {
    hidden: { opacity: 0, y: 24 },
    show: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.7, delay: i * 0.1, ease: EASE },
    }),
};

const staggerList: Variants = {
    hidden: {},
    show: { transition: { staggerChildren: 0.1 } },
};

const WhatsAppIcon: React.FC<{ className?: string }> = ({ className }) => (
    <span aria-hidden className="inline-flex">
        <BrandMark brand="whatsapp" className={className} />
    </span>
);

const CtaButtons: React.FC<{ placement: Placement; company: string; className?: string }> = ({
    placement,
    company,
    className = '',
}) => (
    <div className={`flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 ${className}`}>
        <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track('cta_book_call_click', { placement, company })}
            className="btn-primary rounded-full group"
        >
            Book a 30-min call
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </a>
        <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track('cta_whatsapp_click', { placement, company })}
            className="btn-ghost rounded-full"
        >
            <WhatsAppIcon className="w-5 h-5" />
            Chat on WhatsApp
        </a>
    </div>
);

const RealEstatePage: React.FC = () => {
    const [company] = useState(readCompany);
    const heroRef = useRef<HTMLElement>(null);
    const finalCtaRef = useRef<HTMLElement>(null);
    const [showStickyBar, setShowStickyBar] = useState(false);

    // Tab title and share tags. Crawlers get the same values from the static
    // build/real-estate/index.html (scripts/prerender-routes.js).
    useEffect(() => {
        const previousTitle = document.title;
        document.title = META.title;
        const restorers = (
            [
                ['name="description"', META.description],
                ['property="og:url"', META.url],
                ['property="og:title"', META.title],
                ['property="og:description"', META.description],
                ['property="og:image"', META.image],
                ['name="twitter:title"', META.title],
                ['name="twitter:description"', META.description],
                ['name="twitter:image"', META.image],
            ] as const
        ).map(([attr, content]) => {
            const tag = document.querySelector<HTMLMetaElement>(`meta[${attr}]`);
            const previous = tag?.content;
            if (tag) tag.content = content;
            return () => {
                if (tag && previous !== undefined) tag.content = previous;
            };
        });
        return () => {
            document.title = previousTitle;
            restorers.forEach((restore) => restore());
        };
    }, []);

    // Mobile sticky bar: shown once the hero is scrolled past, hidden again
    // from the final call-to-action down, where its buttons are on screen.
    useEffect(() => {
        const hero = heroRef.current;
        const finalCta = finalCtaRef.current;
        if (!hero || !finalCta) return;

        let pastHero = false;
        let reachedFinalCta = false;
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(({ target, isIntersecting, boundingClientRect }) => {
                if (target === hero) pastHero = !isIntersecting && boundingClientRect.top < 0;
                else reachedFinalCta = isIntersecting || boundingClientRect.top < 0;
            });
            setShowStickyBar(pastHero && !reachedFinalCta);
        });
        observer.observe(hero);
        observer.observe(finalCta);
        return () => observer.disconnect();
    }, []);

    const videoFallback =
        process.env.NODE_ENV === 'development' ? (
            <span>
                Set <code className="text-brand-cyan">VIDEO_URL</code> in src/lib/videos.ts
            </span>
        ) : (
            'Demo video coming soon'
        );

    return (
        <>
            {/* 1 ─ Hero + demo video */}
            <section id="top" ref={heroRef} className="relative pt-24 sm:pt-28 pb-16 sm:pb-24 overflow-hidden">
                <div className="absolute inset-0 bg-grid-fade pointer-events-none" />
                <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-brand-blue/25 rounded-full blur-[140px] opacity-40 animate-aurora pointer-events-none" />
                <div
                    className="absolute top-1/2 -right-40 w-[600px] h-[600px] bg-brand-cyan/15 rounded-full blur-[130px] opacity-30 animate-aurora pointer-events-none"
                    style={{ animationDelay: '-6s' }}
                />
                {/* let the glow fade out instead of ending at the section edge */}
                <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-b from-transparent to-navy-900 pointer-events-none" />

                <div className="container-x relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <motion.div variants={fadeUp} custom={0} initial="hidden" animate="show">
                            <span className="inline-flex items-center gap-2 max-w-full px-3.5 py-1.5 rounded-full glass text-sm font-medium text-text-main">
                                <Building2 className="w-4 h-4 shrink-0 text-brand-cyan" />
                                <span className="min-w-0 break-words">
                                    {company ? `Prepared for ${company}` : 'For real estate developers'}
                                </span>
                            </span>
                        </motion.div>

                        <motion.h1
                            variants={fadeUp}
                            custom={1}
                            initial="hidden"
                            animate="show"
                            className="mt-4 sm:mt-5 font-display text-[2rem] leading-[1.1] sm:text-5xl lg:text-[3.75rem] lg:leading-[1.05] font-bold text-white tracking-tight text-balance"
                        >
                            Watch our AI agent call a buyer <span className="text-gradient">within 2 minutes.</span>
                        </motion.h1>

                        <motion.p
                            variants={fadeUp}
                            custom={2}
                            initial="hidden"
                            animate="show"
                            className="mt-3 sm:mt-5 text-base sm:text-lg md:text-xl text-text-muted max-w-2xl mx-auto leading-relaxed text-balance"
                        >
                            Every enquiry from your ads gets a call in under two minutes, day or night. Watch it happen
                            below.
                        </motion.p>
                    </div>

                    {/* from lg up the player shrinks with short viewports so the whole frame stays above the fold */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.25, ease: EASE }}
                        className="mt-6 sm:mt-10 max-w-4xl lg:max-w-[clamp(40rem,calc((100vh_-_26rem)*16/9),56rem)] mx-auto"
                    >
                        <DemoVideo
                            url={VIDEO_URL}
                            poster={POSTER_URL || undefined}
                            title="AI agent calling a property buyer"
                            fallback={videoFallback}
                            onMilestone={(milestone, provider) => track(milestone, { provider, company })}
                        />
                    </motion.div>

                    <motion.div variants={fadeUp} custom={4} initial="hidden" animate="show">
                        <CtaButtons placement="hero" company={company} className="mt-8 sm:mt-10" />
                    </motion.div>
                </div>
            </section>

            {/* 2 ─ The offer */}
            <section className="relative py-20 md:py-28">
                <div className="container-x">
                    <Reveal className="max-w-4xl mx-auto text-center">
                        <span className="eyebrow mb-4">
                            <span className="h-px w-6 bg-brand-cyan/60" />
                            The offer
                            <span className="h-px w-6 bg-brand-cyan/60" />
                        </span>
                        <h2 className="font-display text-[1.75rem] leading-[1.2] sm:text-4xl md:text-[2.75rem] md:leading-[1.15] font-bold text-white tracking-tight text-balance">
                            What if I told you I have a system that can deliver{' '}
                            <span className="text-gradient">10 to 20 interested buyers</span> to you every month?
                        </h2>
                    </Reveal>

                    <motion.ol
                        variants={staggerList}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: '-60px' }}
                        className="mt-14 md:mt-16 grid md:grid-cols-3 gap-5"
                    >
                        {steps.map((step, i) => (
                            <motion.li key={step.title} variants={itemVariants} className="relative">
                                <div className="group h-full rounded-2xl card-surface p-7 transition-all duration-300 hover:-translate-y-1 hover:border-white/[0.14]">
                                    <div className="flex items-center justify-between">
                                        <span className="grid place-items-center w-11 h-11 rounded-full bg-gradient-to-br from-brand-cyan to-brand-blue font-display text-lg font-bold text-navy-900 ring-4 ring-navy-900 shadow-glow-cyan">
                                            {i + 1}
                                        </span>
                                        <span className="grid place-items-center w-11 h-11 rounded-xl bg-white/[0.04] border border-white/[0.06] text-brand-cyan">
                                            <step.icon className="w-5 h-5" strokeWidth={1.75} />
                                        </span>
                                    </div>
                                    <h3 className="mt-6 font-display text-xl font-bold text-white">{step.title}</h3>
                                    <p className="mt-2.5 text-text-muted leading-relaxed">{step.body}</p>
                                </div>
                            </motion.li>
                        ))}
                    </motion.ol>
                </div>
            </section>

            {/* 3 ─ Any hour */}
            <section className="relative py-20 md:py-28 bg-navy-800/40 border-y border-white/[0.05]">
                <div className="container-x">
                    <SectionHeading
                        eyebrow="Any hour"
                        title={
                            <>
                                A call in under two minutes, <span className="text-gradient">day or night.</span>
                            </>
                        }
                    />

                    <Reveal className="mt-12 max-w-3xl mx-auto">
                        <ol className="rounded-3xl glass p-2.5 sm:p-4 space-y-2.5 sm:space-y-3">
                            {callTimes.map((row) => (
                                <li
                                    key={row.form}
                                    className="flex items-center gap-3 sm:gap-5 rounded-2xl bg-navy-900/70 border border-white/[0.06] px-3 py-3.5 sm:px-6 sm:py-5"
                                >
                                    <span
                                        aria-hidden
                                        className={`grid place-items-center w-9 h-9 sm:w-12 sm:h-12 shrink-0 rounded-xl bg-white/[0.04] border border-white/[0.06] ${row.tint}`}
                                    >
                                        <row.icon className="w-[18px] h-[18px] sm:w-6 sm:h-6" strokeWidth={1.75} />
                                    </span>

                                    <div className="shrink-0">
                                        <div className="flex items-center gap-1 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-text-faint">
                                            <ClipboardList aria-hidden className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                            Form
                                        </div>
                                        <div className="mt-0.5 font-display text-[0.95rem] sm:text-xl font-bold text-white whitespace-nowrap">
                                            {row.form}
                                        </div>
                                    </div>

                                    <div className="relative flex-1 min-w-[2.5rem] flex items-center">
                                        <span className="h-px flex-1 bg-gradient-to-r from-brand-cyan/10 via-brand-cyan to-brand-blue/10 bg-[length:200%_100%] animate-shimmer" />
                                        <ArrowRight aria-hidden className="-ml-1.5 w-3.5 h-3.5 text-brand-blue shrink-0" />
                                        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-navy-900 border border-brand-cyan/30 px-1.5 sm:px-2.5 py-0.5 font-mono text-[10px] sm:text-xs text-brand-cyan whitespace-nowrap">
                                            2 min
                                        </span>
                                    </div>

                                    <div className="shrink-0 text-right">
                                        <div className="flex items-center justify-end gap-1 text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-text-faint">
                                            <PhoneCall aria-hidden className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                                            Call
                                        </div>
                                        <div className="mt-0.5 font-display text-[0.95rem] sm:text-xl font-bold text-gradient whitespace-nowrap">
                                            {row.call}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ol>
                        <p className="mt-8 text-center text-lg md:text-xl text-text-main text-balance">
                            No missed enquiries after office hours, on weekends or during inspections.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* 4 ─ What the agent handles */}
            <section className="relative py-20 md:py-28">
                <div className="container-x">
                    <SectionHeading
                        eyebrow="On every call"
                        title={
                            <>
                                What the agent <span className="text-gradient">handles</span>
                            </>
                        }
                    />

                    <RevealGroup className="mt-12 md:mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                        {agentTasks.map((task) => (
                            <RevealItem key={task.lead}>
                                <div className="group h-full flex gap-4 rounded-2xl card-surface p-6 transition-all duration-300 hover:-translate-y-1 hover:border-white/[0.14]">
                                    <span className="grid place-items-center w-11 h-11 shrink-0 rounded-xl bg-brand-cyan/10 border border-brand-cyan/20 text-brand-cyan group-hover:scale-110 transition-transform duration-300">
                                        <task.icon className="w-5 h-5" strokeWidth={1.75} />
                                    </span>
                                    <p className="text-text-muted leading-relaxed">
                                        <span className="font-semibold text-white">{task.lead}</span> {task.rest}
                                    </p>
                                </div>
                            </RevealItem>
                        ))}
                    </RevealGroup>
                </div>
            </section>

            {/* 5 ─ Who does what */}
            <section className="relative py-20 md:py-28 bg-navy-800/40 border-y border-white/[0.05]">
                <div className="container-x">
                    <SectionHeading
                        eyebrow="Who does what"
                        title={
                            <>
                                What you do vs <span className="text-gradient">what we do</span>
                            </>
                        }
                    />

                    <RevealGroup className="mt-12 md:mt-14 max-w-5xl mx-auto grid md:grid-cols-[1.35fr_1fr] gap-5">
                        <RevealItem>
                            <div className="h-full rounded-2xl p-px bg-gradient-to-br from-brand-cyan/70 via-sky-400/20 to-brand-blue/60">
                                <div className="h-full rounded-[15px] bg-navy-900 p-7 sm:p-8">
                                    <div className="flex items-center justify-between gap-4">
                                        <h3 className="font-display text-2xl font-bold text-white">We handle</h3>
                                        <span className="grid place-items-center w-8 h-8 shrink-0 rounded-xl bg-gradient-to-br from-brand-cyan to-brand-blue text-white shadow-glow-blue">
                                            <Zap className="w-4 h-4 fill-white" />
                                        </span>
                                    </div>
                                    <ul className="mt-6 grid sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 gap-3">
                                        {weHandle.map((item) => (
                                            <li
                                                key={item}
                                                className="flex items-center gap-3 rounded-xl bg-white/[0.03] border border-white/[0.06] px-4 py-3 text-text-main font-medium"
                                            >
                                                <span className="grid place-items-center w-6 h-6 shrink-0 rounded-full bg-brand-cyan/15 text-brand-cyan">
                                                    <Check className="w-3.5 h-3.5" strokeWidth={3} />
                                                </span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </RevealItem>

                        <RevealItem>
                            <div className="h-full rounded-2xl card-surface p-7 sm:p-8">
                                <div className="flex items-center justify-between gap-4">
                                    <h3 className="font-display text-2xl font-bold text-white">Your team handles</h3>
                                    <span className="grid place-items-center w-8 h-8 shrink-0 rounded-xl bg-brand-orange/10 border border-brand-orange/25 text-brand-orange">
                                        <Users className="w-4 h-4" />
                                    </span>
                                </div>
                                <ul className="mt-6 space-y-3">
                                    {youHandle.map((item) => (
                                        <li
                                            key={item}
                                            className="flex items-center gap-3 rounded-xl bg-white/[0.03] border border-white/[0.06] px-4 py-3 text-text-main font-medium"
                                        >
                                            <span className="grid place-items-center w-6 h-6 shrink-0 rounded-full bg-brand-orange/15 text-brand-orange">
                                                <Check className="w-3.5 h-3.5" strokeWidth={3} />
                                            </span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </RevealItem>
                    </RevealGroup>
                </div>
            </section>

            {/* 6 ─ How we start */}
            <section className="relative py-20 md:py-28">
                <div className="container-x">
                    <Reveal className="relative max-w-4xl mx-auto overflow-hidden rounded-3xl card-surface px-6 py-12 sm:px-12 sm:py-16 text-center">
                        <div className="absolute inset-0 bg-grid bg-[length:48px_48px] opacity-50 [mask-image:radial-gradient(ellipse_at_center,#000_20%,transparent_75%)] pointer-events-none" />
                        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[520px] h-[260px] bg-brand-cyan/20 rounded-full blur-[100px] pointer-events-none" />

                        <div className="relative">
                            <span className="eyebrow mb-4">
                                <span className="h-px w-6 bg-brand-cyan/60" />
                                How we start
                                <span className="h-px w-6 bg-brand-cyan/60" />
                            </span>
                            <h2 className="font-display text-3xl md:text-[2.75rem] md:leading-[1.1] font-bold text-white tracking-tight text-balance">
                                Start with a <span className="text-gradient">30-day pilot</span> on one estate.
                            </h2>
                            <p className="mt-5 text-lg md:text-xl text-text-muted leading-relaxed max-w-2xl mx-auto text-balance">
                                You fund the ad budget directly; we set up and run everything else.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* 7 ─ FAQ */}
            <div className="border-t border-white/[0.05]">
                <FAQ id="real-estate-faq" items={faqs} subtitle={null} className="py-20 md:py-28" />
            </div>

            {/* 8 ─ Final CTA */}
            <section ref={finalCtaRef} className="relative py-20 md:py-28 overflow-hidden">
                <div className="absolute inset-0 bg-grid-fade [mask-image:radial-gradient(ellipse_70%_60%_at_50%_100%,#000_30%,transparent_100%)] pointer-events-none" />
                <div className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-[900px] h-[500px] bg-brand-blue/15 rounded-full blur-[140px] opacity-50 pointer-events-none" />

                <div className="container-x relative">
                    <Reveal className="max-w-3xl mx-auto text-center">
                        <h2 className="font-display text-3xl sm:text-4xl md:text-5xl md:leading-[1.1] font-bold text-white tracking-tight text-balance">
                            Ready to stop losing buyers to <span className="text-gradient">slow follow-up?</span>
                        </h2>
                        <CtaButtons placement="final" company={company} className="mt-9" />
                    </Reveal>
                </div>
            </section>

            {/* Mobile sticky booking bar */}
            <AnimatePresence>
                {showStickyBar && (
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ duration: 0.25, ease: EASE }}
                        className="md:hidden fixed inset-x-0 bottom-0 z-40 border-t border-white/[0.08] bg-navy-950/85 backdrop-blur-xl px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
                    >
                        <div className="flex items-center gap-3">
                            <a
                                href={CALENDLY_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => track('cta_book_call_click', { placement: 'sticky', company })}
                                className="btn-primary rounded-full flex-1 py-3"
                            >
                                Book a call
                                <ArrowRight className="w-4 h-4" />
                            </a>
                            <a
                                href={WHATSAPP_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Chat on WhatsApp"
                                onClick={() => track('cta_whatsapp_click', { placement: 'sticky', company })}
                                className="grid place-items-center w-12 h-12 shrink-0 rounded-full bg-[#25D366]/10 border border-[#25D366]/40 hover:bg-[#25D366]/20 transition-colors"
                            >
                                <WhatsAppIcon className="w-6 h-6" />
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default RealEstatePage;
